import { LanguageOption } from '@/config/config';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { extractLocaleFromPathname } from './extractLocaleFromURL';
import { useMessages } from './messagesContext';
import { DirectionProvider } from '@radix-ui/react-direction';

type I18nContextValue = {
  locale: LanguageOption;
  setLocale: (locale: string) => void;
  defaultLocale: LanguageOption;
  availableLocales: LanguageOption[];
  localizeLink: (link: string, targetLocale?: string) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function useI18n() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18nContext must be used within a I18nContextProvider');
  }
  return context;
}

type Props = {
  children: React.ReactNode;
  locale: LanguageOption;
  defaultLocale: LanguageOption;
  availableLocales: LanguageOption[];
};

export function I18nContextProvider({ children, locale, defaultLocale, availableLocales }: Props) {
  // On the initial (SSR) render the loader returns nothing, so we use the messages provided via
  // MessagesProvider (server-rendered / fetched before hydration) - this matches the SSR HTML and
  // avoids a hydration mismatch. On a client-side language switch the (client-only) loader provides
  // the target locale's messages, which take precedence.
  const contextMessages = useMessages();
  const loaderData = useLoaderData() as { messages?: Record<string, string> } | null;
  const messages = loaderData?.messages ?? contextMessages;
  const location = useLocation();
  const navigate = useNavigate();

  const localizeLink = useCallback(
    (link: string, targetLocale: string = locale.code) => {
      const currentLocale = extractLocaleFromPathname(
        link,
        availableLocales.map((localOption) => localOption.code),
        defaultLocale.code
      );
      // If full url with http, then consider it hardcoded and do not localize, but just use it as is. It is probably an external link
      // this has been added to accomodate the case where a hosted portal is referencing a dataset outside our control
      if (/^https?:\/\//.test(link)) {
        return link;
      }

      // Handle root links
      const currentPath = currentLocale === defaultLocale.code ? '/' : `/${currentLocale}`;
      const tagetPath = targetLocale === defaultLocale.code ? '/' : `/${targetLocale}`;

      if (link === currentPath) return link.replace(currentPath, tagetPath);

      // Handle sub pages
      const currentPrefix = currentLocale === defaultLocale.code ? '/' : `/${currentLocale}/`;
      const targetPrefix = targetLocale === defaultLocale.code ? '/' : `/${targetLocale}/`;

      // Relative links that do not start with the "currentPrefix" and should not be localized
      if (!link.startsWith(currentPrefix)) return link;

      return link.replace(currentPrefix, targetPrefix);
    },
    [availableLocales, defaultLocale.code, locale.code]
  );

  const setLocale = useCallback(
    (locale: string) => {
      let targetLink = localizeLink(location.pathname, locale);
      if (location.search) targetLink += location.search;
      navigate(targetLink);
    },
    [navigate, location.pathname, location.search, localizeLink]
  );

  const value: I18nContextValue = useMemo(
    () => ({
      locale,
      setLocale,
      defaultLocale,
      localizeLink,
      availableLocales,
    }),
    [locale, defaultLocale, setLocale, availableLocales, localizeLink]
  );

  useEffect(() => {
    const targetLink = localizeLink(location.pathname, locale.code);

    if (location.pathname !== targetLink) {
      navigate(targetLink);
    }
  }, [locale, navigate, location.pathname, availableLocales, defaultLocale, localizeLink]);

  useEffect(() => {
    const root = document.getElementById('app') ?? document.getElementById('root');
    if (root) root.setAttribute('dir', locale.textDirection);
  }, [locale.textDirection]);

  return (
    <I18nContext.Provider value={value}>
      <Helmet>
        <html lang={locale.localeCode} dir={locale.textDirection} />
      </Helmet>
      <IntlProvider
        messages={messages || {}}
        locale={locale.reactIntlLocale ?? locale.code}
        defaultLocale={defaultLocale.reactIntlLocale ?? defaultLocale.code}
        // Format all dates in UTC so SSR (server tz) and hydration (user tz) produce identical output
        timeZone="UTC"
      >
        <DirectionProvider dir={locale.textDirection}>{children}</DirectionProvider>
      </IntlProvider>
    </I18nContext.Provider>
  );
}
